<script>
  import { 
    format_date, 
    format_utc_date_as_local, 
    update_utc_date_preserve_time 
  } from "#lib/util.js";

  let { 
    is_editing, 
    value: utc_timestamp = $bindable(),
    onchange, 
    variant = 'brand subtle-outlined',
    min = null,
    max = null,
    class: css_class,
    ...rest 
  } = $props();

  let local_date_str = $state('');
  let is_input_focused = $state(false);

  /**
   * Converts UTC timestamp to local date string (YYYY-MM-DD) when the UTC value changes
   * Example: "2024-03-14T10:00:00Z" -> "2024-03-14" (in user's timezone)
   * 
   * In detail: We use a separate date_value state instead of binding directly to value because:
   * 1. The 'value' prop is in UTC format (e.g. "2024-03-14T10:00:00Z")
   * 2. The date input expects local date in YYYY-MM-DD format
   * 3. We need to handle timezone conversions between UTC and local time
   * 
   * The native date input has limitations with real-time updates:
   * - Changes via the date picker UI only register after blur
   * - Limited control over styling and behavior
   * Future enhancement: Replace with MonthSelector component using Popover
   * for better UX and more control over the date selection experience
   */
  $effect(() => {
    if (utc_timestamp) {
      local_date_str = format_utc_date_as_local(utc_timestamp);
    }
  });

  /**
   * Converts local date back to UTC when input changes, preserving the original time
   * Example: If original was "2024-03-14T10:00:00Z" and date changes to "2024-03-15",
   * result will be "2024-03-15T10:00:00Z"
   * 
   * In detail: The submit_update function handles:
   * 1. Converting local date back to UTC while preserving the time
   * 2. Debouncing updates during focus to prevent partial input issues
   * 3. Triggering the onchange callback with the new UTC value
   * 
   * We can't use a direct two-way binding because:
   * - We need to coordinate between focus state and value updates
   * - The conversion between UTC and local time needs to happen at specific points
   * - We want to preserve the time portion of the original UTC timestamp
   */
  function submit_update() {
    if (is_input_focused || !local_date_str) return;
    
    utc_timestamp = update_utc_date_preserve_time(local_date_str, utc_timestamp);
    
    if (onchange) {
      onchange(utc_timestamp);
    }
  }
</script>

{#if is_editing}
  <input
    type="date"
    value={local_date_str}
    onchange={(e) => {
      local_date_str = e.target.value;
      submit_update();
    }}
    onblur={() => {
      is_input_focused = false;
      submit_update();
    }}
    onfocus={() => is_input_focused = true}
    class="text-control {css_class} {variant} {variant.includes('outlined') ? variant.includes('system') ? 'border-1-system' : 'border-1' : ''}"
    {min}
    {max}
    {...rest}
  />
{:else}
  {#if utc_timestamp}
    {#key utc_timestamp}
      {format_date(utc_timestamp, 'DMY')}
    {/key}
  {:else}
    ----
  {/if}
{/if}

<style lang='postcss'>
  input[type=date] {
    width: fit-content;
  }
  :global(::-webkit-datetime-edit-month-field) {
    color: var(--color-default-foreground); 
    text-transform: uppercase 
  }
  :global(::-webkit-datetime-edit-day-field) { 
    color: var(--color-default-foreground); 
    text-transform: uppercase 
  }
  :global(::-webkit-datetime-edit-year-field) { 
    color: var(--color-default-foreground); 
    text-transform: uppercase 
  }
</style>